import { downloadSheet, postForm, postJson } from "./http";

/**
 * The outstanding invoices & bills import.
 *
 * A migrating client arrives with documents unpaid as of their account start
 * date. The CA downloads an Excel template, lists every unpaid customer
 * invoice — plus a credit-note row for any advance-holding customer — or every
 * unpaid vendor bill plus vendor-credit rows, and uploads it back. We validate
 * against the synced Zoho contact book, stage the rows locally, and one push
 * turns them into real documents in the client's Zoho Books. The whole
 * reasoning lives in docs/onboarding-outstanding-invoices.md.
 *
 */

/** Which sheet this is: "invoice" is the customer one, "bill" the vendor one. */
export type OutstandingKind = "invoice" | "bill";

/**
 * What one row becomes in Zoho.
 *
 * The credit shapes are the advance-holders: a customer or vendor who is
 * net-negative on the start date goes in as a credit note or vendor credit,
 * never as a negative amount.
 */
export type OutstandingDocType = "invoice" | "credit_note" | "bill" | "vendor_credit";

/** What the diff decided about one row of the sheet. */
export type OutstandingAction = "new" | "update" | "unchanged" | "invalid";

/**
 * How far a row got.
 *
 * `skipped` is not a failure and not a success: an unchanged row or an invalid
 * one. `failed` is Zoho saying no, and only those are worth retrying.
 */
export type OutstandingRowStatus =
  | "pending"
  | "created"
  | "updated"
  | "skipped"
  | "failed"
  /**
   * Zoho already held a document with this number, so nothing was sent. Not a
   * failure — the books already say what the sheet says — and not a creation
   * either. The row is matched to the document that was already there.
   */
  | "duplicate";

/**
 * Where the import as a whole has got to.
 *
 * `committing` covers both "a batch is running" and "a batch finished and there
 * is more to do" — the push is a series of round trips, and between them the
 * import sits here.
 */
export type OutstandingImportStatus = "validated" | "committing" | "done" | "cancelled";

/**
 * Something wrong with a row, or worth knowing about it.
 *
 * `error` blocks that row and nothing else. `warning` blocks nothing — it is a
 * decision rather than a mistake.
 */
export interface OutstandingIssue {
  /** The column heading, so the message points at a cell. */
  field: string;
  message: string;
  severity: "error" | "warning";
}

export interface OutstandingRowDTO {
  id: number;
  /** The line number in the CA's own spreadsheet, not our ordering. */
  sheet_row_no: number;
  doc_type: OutstandingDocType;
  contact_name: string | null;
  document_number: string | null;
  document_date: string | null;
  due_date: string | null;
  /** Gross outstanding as of the start date, GST included, never recomputed. */
  amount: number | null;
  /**
   * The legacy book rate, mandatory on an FX contact's row and forbidden on an
   * INR one — omitting it would have Zoho book the live market rate of the day,
   * which is silently wrong either way.
   */
  exchange_rate: number | null;
  currency_code: string | null;
  action: OutstandingAction;
  status: OutstandingRowStatus;
  /** Field-level before and after, for the rows that change something. */
  changes: Record<string, { from: unknown; to: unknown }> | null;
  issues: OutstandingIssue[];
  /** Zoho's own words, when it refused this one. */
  error: string | null;
  /** The sheet's own cells, so a row can be shown the way its author typed it. */
  raw: Record<string, string | null>;
}

export interface OutstandingImportDTO {
  id: number;
  kind: OutstandingKind;
  original_filename: string;
  status: OutstandingImportStatus;
  total_rows: number;
  new_rows: number;
  changed_rows: number;
  unchanged_rows: number;
  invalid_rows: number;
  /** Outcomes. Zero until a push has run. */
  created_rows: number;
  updated_rows: number;
  failed_rows: number;
  /** Rows Zoho already held under that number. Nothing was sent for these. */
  duplicate_rows: number;
  created_at: string | null;
  committed_at: string | null;
  /**
   * This exact file has been pushed before. Not a block — re-running after a
   * partial failure is legitimate — and gentler here than on the other sheets:
   * Zoho refuses a repeated document number outright (code 1001), so a re-push
   * bounces off what already landed rather than silently doubling it.
   */
  already_pushed_at: string | null;
  /**
   * A DIFFERENT client record of ours has already pushed this kind of sheet
   * into the same Zoho organisation, and when. Two client records on one set of
   * books is legitimate — a re-run, a hand-over, a test — but the CA about to
   * write to those books should hear it before the push, not from a screen full
   * of "already exists" afterwards.
   */
  org_pushed_at: string | null;
}

/**
 * What one call to commit did.
 *
 * Present only on a commit response — a plain read of the review has no batch
 * to report on.
 */
export interface OutstandingPushDTO {
  status: "committing" | "done";
  /**
   * The run stopped for a reason that will still be true if called again
   * straight away — a rate limit, or credentials Zoho refused. The caller must
   * NOT loop on this; it is a wait-and-retry, not a continue.
   */
  halted: boolean;
  halted_reason: string | null;
  /** Rows this batch attempted. */
  attempted: number;
  /**
   * Rows still to SEND — never-attempted ones, plus any document created but
   * not yet marked sent. Refused rows are not counted: they are not retried by
   * a continue, so counting them would put a number on the button that never
   * goes down.
   */
  remaining: number;
  created: number;
  updated: number;
  failed: number;
  /** Rows Zoho already held under that number. */
  duplicate: number;
}

/** `import` is null whenever there is nothing open, which is the ordinary state. */
export interface OutstandingReviewDTO {
  import: OutstandingImportDTO | null;
  rows: OutstandingRowDTO[];
  push?: OutstandingPushDTO;
}

interface ReviewEnvelope {
  success: boolean;
  message: string;
  data: OutstandingReviewDTO;
}

/**
 * Where these endpoints put the sentence a CA should read.
 *
 * Without this, every refusal reached the screen as the envelope's generic
 * "Validation failed" — including "this sheet is part-way into the client's
 * books", which is the difference between a button that looks broken and one
 * that has told you why it will not do that.
 */
const REFUSAL_KEYS = ["public_id", "commit"];

/**
 * The outstanding-documents template as a spreadsheet, saved to their machine.
 *
 * A POST returning a blob rather than a link, because the endpoint needs the
 * bearer token and an `<a href>` cannot carry one. The object URL is revoked
 * straight after: without that, the whole file stays in memory until the tab is
 * closed.
 */
export const downloadOutstandingTemplate = (publicId: string, kind: OutstandingKind): Promise<void> =>
  downloadSheet(
    "/api/onboarding/outstanding/template",
    { public_id: publicId, kind },
    `outstanding-${kind === "invoice" ? "invoices" : "bills"}.xlsx`
  );

/**
 * Send the filled-in sheet up and get back what it would change.
 *
 * The server puts the readable refusal under errors.file — "that sheet has no
 * header row" rather than "HTTP 400".
 */
export async function uploadOutstandingSheet(
  publicId: string,
  kind: OutstandingKind,
  file: File
): Promise<OutstandingReviewDTO> {
  const form = new FormData();
  form.append("public_id", publicId);
  form.append("kind", kind);
  form.append("file", file);

  return (await postForm<ReviewEnvelope>("/api/onboarding/outstanding/import/upload", form, ["file"])).data;
}

/** The review as it stands. Survives a reload; returns `{import: null}` when there is none. */
export const fetchOutstandingImport = async (
  publicId: string,
  kind: OutstandingKind
): Promise<OutstandingReviewDTO> =>
  (
    await postJson<ReviewEnvelope>(
      "/api/onboarding/outstanding/import/get",
      { public_id: publicId, kind },
      REFUSAL_KEYS
    )
  ).data;

/** THROWS THE STAGED ROWS AWAY and deletes the uploaded file. */
export const cancelOutstandingImport = async (
  publicId: string,
  kind: OutstandingKind
): Promise<OutstandingReviewDTO> =>
  (
    await postJson<ReviewEnvelope>(
      "/api/onboarding/outstanding/import/cancel",
      { public_id: publicId, kind },
      REFUSAL_KEYS
    )
  ).data;

/**
 * Send one batch of the reviewed sheet into the client's Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS AND NOTHING IT DOES CAN BE UNDONE. Every
 * row becomes a real document — an invoice, bill, credit note or vendor credit
 * — and each one costs two Zoho calls, the create and the status call that
 * makes the draft real. A batch is a handful of those in sequence; the caller
 * keeps calling while `push.status` is `committing` — but stops on
 * `push.halted`, which means calling again now would just fail the same way.
 *
 * `acknowledgeDuplicate` corresponds to the CA having been shown that this
 * exact file was pushed before and choosing to send it anyway. The server
 * refuses without it, so it is a decision rather than a default.
 */
export const commitOutstandingImport = async (
  publicId: string,
  kind: OutstandingKind,
  acknowledgeDuplicate = false
): Promise<OutstandingReviewDTO> =>
  (
    await postJson<ReviewEnvelope>(
      "/api/onboarding/outstanding/import/commit",
      { public_id: publicId, kind, acknowledge_duplicate: acknowledgeDuplicate },
      REFUSAL_KEYS
    )
  ).data;

/** Dismiss a finished push. Not an undo — the documents are in Zoho. */
export const closeOutstandingImport = async (
  publicId: string,
  kind: OutstandingKind
): Promise<OutstandingReviewDTO> =>
  (
    await postJson<ReviewEnvelope>(
      "/api/onboarding/outstanding/import/close",
      { public_id: publicId, kind },
      REFUSAL_KEYS
    )
  ).data;
